/**
 * Floating zoom controls — in / out / fit + an editable live percentage
 * (the input is an isolated leaf — see `PercentInput/`).
 *
 * @module FloorPlanEditorZoomControls
 * @category Containers
 */

import { Pressable, View } from 'react-native';
import { Maximize, Minus, Plus } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { Icon } from '@components/ui/icon';

import FloorPlanZoomPercentInput from './PercentInput';

type Props = {
  /** Current zoom, as a whole percentage (e.g. 100). */
  zoomPercent: number;
  /** Fired when the zoom-in button is pressed. */
  onZoomIn: () => void;
  /** Fired when the zoom-out button is pressed. */
  onZoomOut: () => void;
  /** Fired with a target percentage (from the editable percent input). */
  onZoomTo: (percent: number) => void;
  /** Fired when the zoom-to-fit button is pressed. */
  onFit: () => void;
};

/**
 * Floating zoom controls — in / out / fit + an editable live percentage.
 *
 * @category Containers
 * @param zoomPercent - Current zoom, as a whole percentage.
 * @param onZoomIn - Fired when the zoom-in button is pressed.
 * @param onZoomOut - Fired when the zoom-out button is pressed.
 * @param onZoomTo - Fired with a target percentage.
 * @param onFit - Fired when the zoom-to-fit button is pressed.
 */
const FloorPlanZoomControls = ({
  zoomPercent,
  onZoomIn,
  onZoomOut,
  onZoomTo,
  onFit,
}: Props) => {
  const { t } = useTranslation();

  return (
    <View className="bg-card border-border flex-row items-center gap-1 rounded-4xl border p-1.5 shadow-sm shadow-black/5">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('pages:floorPlanEditor.zoomOut')}
        onPress={onZoomOut}
        className="active:bg-muted h-11 w-11 items-center justify-center rounded-full">
        <Icon as={Minus} size={20} className="text-foreground" />
      </Pressable>
      <FloorPlanZoomPercentInput
        zoomPercent={zoomPercent}
        onZoomTo={onZoomTo}
      />
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('pages:floorPlanEditor.zoomIn')}
        onPress={onZoomIn}
        className="active:bg-muted h-11 w-11 items-center justify-center rounded-full">
        <Icon as={Plus} size={20} className="text-foreground" />
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('pages:floorPlanEditor.zoomToFit')}
        onPress={onFit}
        className="active:bg-muted h-11 w-11 items-center justify-center rounded-full">
        <Icon as={Maximize} size={20} className="text-foreground" />
      </Pressable>
    </View>
  );
};

export default FloorPlanZoomControls;
